import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Row,
  Col,
  Spinner,
  ProgressBar,
} from "react-bootstrap";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  addDoc,
  writeBatch,
  doc,
  getFirestore,
  getDocs,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2"; // Importar SweetAlert2
import axios from "axios"; // Importar axios
import { FUNCTION_URLS } from "../../config/functionUrls";

const PdfTextExtractor = ({ ruc }) => {
  // Estado para manejar la carga de PDF.js
  const [pdfJsLoaded, setPdfJsLoaded] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(""); // ✅ Nuevo estado para el título
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Referencia a Firestore
  const db = getFirestore(firebaseApp);

  // Cargar PDF.js dinámicamente
  useEffect(() => {
    const loadPdfJs = async () => {
      try {
        // Limpiar scripts anteriores primero
        const existingScripts = document.querySelectorAll(
          'script[src*="pdfjs-dist"]'
        );
        existingScripts.forEach((script) => {
          try {
            document.body.removeChild(script);
          } catch (e) {
            // Ignorar errores
          }
        });

        const script = document.createElement("script");
        // CAMBIO: Usar versión 2.16.105 que es compatible con el worker
        script.src =
          "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.min.js";
        script.async = true;

        // Añadir atributos adicionales para mejor carga
        script.crossOrigin = "anonymous";
        script.type = "text/javascript";

        const scriptLoaded = new Promise((resolve, reject) => {
          script.onload = () => {
            console.log("PDF.js script cargado exitosamente");
            resolve();
          };
          script.onerror = (error) => {
            console.error("Error cargando PDF.js script:", error);
            reject(new Error("No se pudo cargar el script de PDF.js"));
          };

          // Timeout después de 30 segundos
          setTimeout(() => {
            reject(new Error("Timeout al cargar PDF.js"));
          }, 30000);
        });

        document.head.appendChild(script); // Usar head en lugar de body
        await scriptLoaded;

        // Verificar que pdfjsLib esté disponible
        if (!window.pdfjsLib) {
          throw new Error("PDF.js no se cargó correctamente");
        }

        // CAMBIO: Usar la MISMA versión para el worker (2.16.105)
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js";

        console.log("PDF.js configurado exitosamente");
        setPdfJsLoaded(true);
      } catch (error) {
        console.error("Error al cargar PDF.js:", error);
        setError(
          `Error al cargar la biblioteca PDF.js: ${error.message}. Intente recargar la página.`
        );
      }
    };

    loadPdfJs();

    return () => {
      // Limpiar al desmontar
      const scripts = document.querySelectorAll('script[src*="pdfjs-dist"]');
      scripts.forEach((s) => {
        try {
          if (s.parentNode) {
            s.parentNode.removeChild(s);
          }
        } catch (e) {
          // Ignorar errores
        }
      });
    };
  }, []);

  // Función para mostrar notificaciones
  const showNotification = (title, message, icon = "success") => {
    Swal.fire({
      title: title,
      text: message,
      icon: icon,
      timer: 3000,
      timerProgressBar: true,
      toast: false,
      position: "center",
      showConfirmButton: false,
    });
  };

  // Manejar selección de archivo
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type === "application/pdf") {
        setFile(selectedFile);
        // ✅ Inicializar título con el nombre del archivo (sin extensión)
        setTitle(selectedFile.name.replace(/\.pdf$/i, ""));
        setError(null);
        setSuccess(false);
      } else {
        setError("El archivo debe ser un PDF");
        setFile(null);
        setTitle("");
      }
    }
  };

  // Proceso completo automatizado
  const handleProcessCompleteDocument = async () => {
    if (!file || !ruc) {
      setError("Se requiere un archivo PDF válido");
      return;
    }

    try {
      setProcessing(true);
      setProgress(0);
      setError(null);
      setSuccess(false);

      // 1. Subir archivo
      setStatusMessage("Subiendo documento...");
      const uploadResult = await uploadToFirebase();
      if (!uploadResult) throw new Error("Error al subir archivo");
      setProgress(20);

      // 2. Extraer texto
      setStatusMessage("Analizando contenido del documento...");
      const extractResult = await extractText();
      if (!extractResult) throw new Error("Error al extraer texto");
      setProgress(40);

      // 3. Indexar en base de datos
      setStatusMessage("Preparando documento para consultas...");
      const indexResult = await indexFragmentsToFirestore(
        uploadResult.storagePath,
        extractResult.fragments,
        extractResult.metadata
      );
      if (!indexResult) throw new Error("Error al indexar");
      setProgress(70);

      // 4. Generar embeddings para IA
      setStatusMessage("Optimizando para consultas inteligentes...");
      const embeddingsResult = await processEmbeddings(
        extractResult.metadata.documentId
      );
      if (!embeddingsResult) throw new Error("Error al generar embeddings");
      setProgress(100);

      setStatusMessage("¡Documento procesado exitosamente!");
      setSuccess(true);

      showNotification(
        "¡Documento listo!",
        "El documento está preparado para consultas de IA.",
        "success"
      );
    } catch (error) {
      console.error("Error en procesamiento completo:", error);
      setError(`Error al procesar el documento: ${error.message}`);
      showNotification("Error", error.message, "error");
    } finally {
      setProcessing(false);
    }
  };

  // Función para subir archivo a Firebase Storage
  const uploadToFirebase = async () => {
    if (!file) return null;

    try {
      const storage = getStorage(firebaseApp);
      const fileName = `ai-documents/${uuidv4()}-${file.name}`;
      const fileRef = ref(storage, fileName);

      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);

      return {
        url: downloadUrl,
        storagePath: fileName,
      };
    } catch (error) {
      console.error("Error al subir archivo:", error);
      throw error;
    }
  };

  // Función para extraer texto del PDF (MEJORADA para más fragmentos)
  const extractText = async () => {
    if (!pdfJsLoaded || !window.pdfjsLib) {
      throw new Error("La biblioteca PDF.js no está cargada");
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
      }).promise;

      let fullText = "";
      const fragments = [];
      const structureMap = {};

      const documentMetadata = {
        title: title || file.name.replace(/\.pdf$/, ""), // ✅ Usar título del input o nombre del archivo
        description: "",
        pageCount: pdf.numPages,
        extractionDate: new Date().toISOString(),
        documentId: uuidv4().substring(0, 8),
        terms: {},
        references: [],
        structure: {},
        structurePath: {},
      };

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        const pageText = extractPageText(textContent);
        fullText += pageText + "\n\n";

        // CAMBIO: Crear fragmentos directamente del texto de la página
        // en lugar de depender solo de la estructura analizada
        const pageFragments = createPageFragments(pageText, i, structureMap);
        fragments.push(...pageFragments);

        detectTermsAndReferences(pageText, i, documentMetadata);
      }

      const docIdPrefix = documentMetadata.documentId;
      const enhancedFragments = fragments.map((fragment, index) => ({
        ...fragment,
        id: `${docIdPrefix}_pg${fragment.pageNumber}_f${index}`,
        document_id: documentMetadata.documentId,
        extractionDate: documentMetadata.extractionDate,
        structure_path: fragment.structure_path || null,
        is_partial: fragment.isPartial || false,
        part_index: fragment.partIndex || null,
        total_parts: fragment.totalParts || null,
        structure_type: fragment.structureType || null,
        structure_header: fragment.structureHeader || null,
        paragraph_index: fragment.paragraphIndex || index,
        page: fragment.pageNumber,
        char_count: fragment.text.length,
      }));

      return {
        text: fullText,
        fragments: enhancedFragments,
        pageCount: pdf.numPages,
        metadata: documentMetadata,
      };
    } catch (error) {
      console.error("Error al extraer texto:", error);
      throw error;
    }
  };

  // Función para extraer texto de una página (simplificada)
  const extractPageText = (textContent) => {
    let lastY = -1;
    let text = "";

    const textItems = textContent.items.sort((a, b) => {
      if (Math.abs(a.transform[5] - b.transform[5]) < 5) {
        return a.transform[4] - b.transform[4];
      }
      return b.transform[5] - a.transform[5];
    });

    for (const item of textItems) {
      const currentY = item.transform[5];
      if (lastY !== -1 && Math.abs(currentY - lastY) > 5) {
        text += "\n";
      }

      if (text.length > 0 && !text.endsWith(" ") && !item.str.startsWith(" ")) {
        text += " ";
      }
      text += item.str;

      lastY = currentY;
    }

    return text
      .replace(/\s+\n/g, "\n")
      .replace(/\n\s+/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // Función para analizar estructura del documento (simplificada)
  const analyzeDocumentStructure = (text, pageNumber, structureMap) => {
    const lines = text.split("\n");
    const structuredLines = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (!line) {
        structuredLines.push(line);
        continue;
      }

      let capMatches = [
        ...line.matchAll(/\b(CAPÍTULO|CAPITULO)\s+([IVXLCDM0-9]+)\b/gi),
      ];
      let artMatches = [
        ...line.matchAll(
          /\b(?:Art\.|Artículo|ARTÍCULO|ARTICULO)\s+(\d+)[°º]?\b/gi
        ),
      ];

      if (capMatches.length > 0 || artMatches.length > 0) {
        let lastEnd = 0;
        const allMatches = [
          ...capMatches.map((m) => ({ type: "capitulo", match: m })),
          ...artMatches.map((m) => ({ type: "articulo", match: m })),
        ].sort((a, b) => a.match.index - b.match.index);

        for (const { type, match } of allMatches) {
          if (match.index > lastEnd) {
            const beforeText = line.substring(lastEnd, match.index).trim();
            if (beforeText) structuredLines.push(beforeText);
          }

          if (type === "capitulo") {
            const capNum = match[2];
            const capId = `capitulo_${capNum.toLowerCase()}`;
            structureMap[capId] = {
              type: "capitulo",
              title: match[0],
              pageStart: pageNumber,
              level: 2,
            };
            structuredLines.push(
              `###STRUCTURE_HEADER###${capId}### ${match[0]}`
            );
          } else if (type === "articulo") {
            const artNum = match[1];
            const artId = `articulo_${artNum}`;
            structureMap[artId] = {
              type: "articulo",
              title: match[0],
              pageStart: pageNumber,
              level: 3,
            };
            structuredLines.push(`###ARTICLE###${artId}### ${match[0]}`);
          }

          lastEnd = match.index + match[0].length;
        }

        if (lastEnd < line.length) {
          const afterText = line.substring(lastEnd).trim();
          if (afterText) structuredLines.push(afterText);
        }
      } else {
        structuredLines.push(line);
      }
    }

    return structuredLines.join("\n");
  };

  // NUEVA FUNCIÓN: Crear fragmentos de página completos
  const createPageFragments = (pageText, pageNumber, structureMap) => {
    const fragments = [];

    // Analizar estructura para obtener metadatos
    const structuredText = analyzeDocumentStructure(
      pageText,
      pageNumber,
      structureMap
    );

    // Crear fragmentos de encabezados de estructura
    const headerFragments = createStructureHeaderFragments(
      structuredText,
      pageNumber,
      structureMap
    );
    fragments.push(...headerFragments);

    // CAMBIO IMPORTANTE: Crear fragmentos de TODO el contenido de la página
    // Dividir el texto completo en chunks de 200 caracteres
    const contentFragments = createContentFragments(pageText, pageNumber, 200);
    fragments.push(...contentFragments);

    return fragments;
  };

  // Función para crear fragmentos de encabezados de estructura
  const createStructureHeaderFragments = (
    structuredText,
    pageNumber,
    structureMap
  ) => {
    const fragments = [];
    let currentStructureType = null;
    let currentStructureHeader = null;

    const paragraphs = structuredText
      .split(/\n{2,}/)
      .filter((p) => p.trim().length > 0);

    paragraphs.forEach((paragraph, idx) => {
      if (paragraph.includes("###STRUCTURE_HEADER###")) {
        const structureMatch = paragraph.match(
          /###STRUCTURE_HEADER###([^#]+)###\s*(.*)/
        );
        if (structureMatch) {
          currentStructureHeader = structureMatch[2].trim();
          currentStructureType = structureMap[structureMatch[1]]?.type || null;
          fragments.push({
            text: currentStructureHeader,
            pageNumber,
            paragraphIndex: fragments.length,
            structureType: currentStructureType,
            structureHeader: currentStructureHeader,
            isStructureHeader: true,
          });
        }
      } else if (paragraph.includes("###ARTICLE###")) {
        const articleMatch = paragraph.match(/###ARTICLE###([^#]+)###\s*(.*)/);
        if (articleMatch) {
          currentStructureHeader = articleMatch[2].trim();
          currentStructureType = "articulo";
          fragments.push({
            text: currentStructureHeader,
            pageNumber,
            paragraphIndex: fragments.length,
            structureType: currentStructureType,
            structureHeader: currentStructureHeader,
            isStructureHeader: true,
          });
        }
      }
    });

    return fragments;
  };

  // NUEVA FUNCIÓN: Crear fragmentos de contenido de página
  const createContentFragments = (pageText, pageNumber, chunkSize = 200) => {
    const fragments = [];

    // Limpiar el texto
    const cleanText = pageText
      .replace(/\s+/g, " ")
      .replace(/\s+\./g, ".")
      .replace(/\s+,/g, ",")
      .trim();

    if (!cleanText) return fragments;

    // Dividir en chunks de chunkSize caracteres
    const chunks = splitTextIntoChunks(cleanText, chunkSize);

    chunks.forEach((chunk, index) => {
      fragments.push({
        text: chunk,
        pageNumber,
        paragraphIndex: index,
        structureType: null,
        structureHeader: null,
        isStructureHeader: false,
        isContentFragment: true,
        chunkIndex: index + 1,
        totalChunks: chunks.length,
      });
    });

    return fragments;
  };

  // NUEVA FUNCIÓN: Dividir texto en chunks
  const splitTextIntoChunks = (text, chunkSize) => {
    if (text.length <= chunkSize) return [text];

    const chunks = [];
    let start = 0;

    while (start < text.length) {
      let end = Math.min(start + chunkSize, text.length);

      // Intentar cortar en un espacio si es posible
      if (end < text.length) {
        const lastSpace = text.lastIndexOf(" ", end);
        if (lastSpace > start && lastSpace > end - 50) {
          // No ir muy atrás
          end = lastSpace + 1;
        }
      }

      const chunk = text.substring(start, end).trim();
      if (chunk) {
        chunks.push(chunk);
      }

      start = end;
    }

    return chunks;
  };

  // Función para detectar términos y referencias (simplificada)
  const detectTermsAndReferences = (text, pageNumber, metadata) => {
    const termRegex =
      /(?:"([^"]+)"|\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,3}))[^:]*:\s+([^.]{5,}\.)/g;
    let termMatch;

    while ((termMatch = termRegex.exec(text)) !== null) {
      const term = (termMatch[1] || termMatch[2]).trim();
      if (term.length > 2) {
        metadata.terms[term] = { definition: termMatch[3].trim(), pageNumber };
      }
    }

    const refRegex =
      /(?:conforme|según|de acuerdo|establecido|dispuesto|previsto|indicado)[^.]*(?:artículo|articulo|Art\.|cláusula|clausula|título|titulo|capítulo|capitulo)[^.]*(?:\d+|[IVX]+)/gi;
    let refMatch;

    while ((refMatch = refRegex.exec(text)) !== null) {
      metadata.references.push({ text: refMatch[0].trim(), pageNumber });
    }
  };

  // Función para limpiar texto
  const cleanText = (text) => {
    if (!text) return "";

    return text
      .replace(/\s+/g, " ")
      .replace(/\s+\./g, ".")
      .replace(/\s+,/g, ",")
      .replace(/^\s+|\s+$/g, "")
      .replace(/ {2,}/g, " ")
      .replace(/[^\S\r\n]+/g, " ");
  };

  // Función para indexar fragmentos en Firestore
  const indexFragmentsToFirestore = async (
    storagePath = null,
    fragmentsToIndex = null,
    metadataToUse = null
  ) => {
    try {
      const fragments = fragmentsToIndex || [];
      const metadata = metadataToUse || {};

      const documentData = {
        document_id: metadata.documentId,
        title: metadata.title || "Documento sin título",
        description: metadata.description || "",
        source: "PDF",
        source_filename: file ? file.name : "",
        creation_date: new Date().toISOString(),
        page_count: metadata.pageCount || 0,
        fragment_count: fragments.length,
        status: "active",
        version: "1.0",
        storage_path: storagePath || "",
        last_indexed: new Date().toISOString(),
        ruc: ruc,
      };

      await addDoc(collection(db, "ai_documentos"), documentData);

      const batchSize = 20;
      let batch = writeBatch(db);
      let batchCount = 0;

      const cleanObjectForFirestore = (obj) => {
        const cleanObj = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined && value !== null) {
            cleanObj[key] = value;
          }
        }
        return cleanObj;
      };

      for (const fragment of fragments) {
        const fragmentRef = doc(collection(db, "ai_fragmentos"));
        const fragmentData = cleanObjectForFirestore({
          fragment_id: fragment.id,
          document_id: metadata.documentId,
          page: fragment.pageNumber,
          paragraph_index: fragment.paragraphIndex,
          text: fragment.text,
          char_count: fragment.text.length,
          structure_type: fragment.structureType || null,
          structure_header: fragment.structureHeader || null,
          structure_path: fragment.structure_path || null,
          is_partial: fragment.isPartial === true,
          part_index: fragment.partIndex ? Number(fragment.partIndex) : null,
          total_parts: fragment.totalParts ? Number(fragment.totalParts) : null,
          parent_fragment_id: fragment.parent_fragment_id || null,
          extraction_date: new Date().toISOString(),
          tags: [],
          embedding_id: null,
          ruc: ruc,
        });

        batch.set(fragmentRef, fragmentData);
        batchCount++;

        if (batchCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

      if (metadata.structurePath) {
        const structureBatch = writeBatch(db);
        for (const [pathKey, structureInfo] of Object.entries(
          metadata.structurePath
        )) {
          const structureId = `${metadata.documentId}_${pathKey}`;
          const structureRef = doc(
            collection(db, "ai_estructura_documento"),
            structureId
          );
          structureBatch.set(
            structureRef,
            cleanObjectForFirestore({
              doc_id: metadata.documentId,
              structure_id: structureId,
              parent_id: null,
              type: structureInfo.type,
              title: structureInfo.title,
              description: "",
              level:
                structureInfo.type === "titulo"
                  ? 1
                  : structureInfo.type === "capitulo"
                  ? 2
                  : structureInfo.type === "articulo"
                  ? 3
                  : 2,
              order: 0,
              path: pathKey,
              page_start: structureInfo.pageStart || null,
              page_end: null,
              fragments: structureInfo.fragments || [],
            })
          );
        }
        await structureBatch.commit();
      }

      if (metadata.terms && Object.keys(metadata.terms).length > 0) {
        const termsBatch = writeBatch(db);
        let termCount = 0;
        for (const [term, termInfo] of Object.entries(metadata.terms)) {
          const termId = `term_${metadata.documentId}_${termCount}`;
          const termRef = doc(collection(db, "ai_terminos_definidos"), termId);
          termsBatch.set(
            termRef,
            cleanObjectForFirestore({
              term_id: termId,
              document_id: metadata.documentId,
              term: term,
              definition: termInfo.definition,
              defined_in_fragment_id: null,
              page: termInfo.pageNumber || 1,
              used_in_fragments: [],
            })
          );
          termCount++;
        }
        if (termCount > 0) {
          await termsBatch.commit();
        }
      }

      if (metadata.references && metadata.references.length > 0) {
        const refBatch = writeBatch(db);
        metadata.references.forEach((ref, idx) => {
          const refId = `ref_${metadata.documentId}_${idx}`;
          const refRef = doc(collection(db, "ai_referencias_cruzadas"), refId);
          refBatch.set(
            refRef,
            cleanObjectForFirestore({
              reference_id: refId,
              document_id: metadata.documentId,
              source_fragment_id: null,
              target_structure: null,
              reference_text: ref.text,
              page: ref.pageNumber || 1,
              type: "article_reference",
            })
          );
        });
        await refBatch.commit();
      }

      return true;
    } catch (error) {
      console.error("Error al indexar en Firestore:", error);
      throw error;
    }
  };

  // Función para procesar embeddings
  const processEmbeddings = async (documentId) => {
    try {
      const functionUrl =
        FUNCTION_URLS.ragProcessEmbeddings;

      let continueProcessing = true;
      let totalProcessed = 0;
      const batchSize = 50;
      let noFragmentsFound = false;

      while (continueProcessing) {
        try {
          const response = await axios.post(functionUrl, {
            documentId: documentId,
            batchSize: batchSize,
            namespace: ruc, // Usar RUC como namespace en Pinecone
            ruc: ruc,
          });

          const data = response.data;

          if (data.success) {
            if (data.processed > 0) {
              totalProcessed += data.processed;
            } else {
              continueProcessing = false;
            }
          } else {
            if (
              data.message &&
              data.message.includes("No se encontraron fragmentos pendientes")
            ) {
              noFragmentsFound = true;
              continueProcessing = false;
            } else {
              throw new Error(data.message || "Error desconocido");
            }
          }
        } catch (error) {
          if (
            error.message &&
            error.message.includes("No se encontraron fragmentos pendientes")
          ) {
            noFragmentsFound = true;
            continueProcessing = false;
          } else {
            throw error;
          }
        }
      }

      return true;
    } catch (error) {
      console.error("Error al procesar embeddings:", error);
      throw error;
    }
  };

  // Renderizar componente
  return (
    <Container>
      <Alert variant="info" className="mb-3">
        <small>
          Procesando documento para la empresa con RUC: <strong>{ruc}</strong>
        </small>
      </Alert>

      <Card className="mb-4">
        <Card.Header>
          <h5>📄 Procesar Documento para Consultas IA</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Seleccionar Documento PDF</Form.Label>
            <Form.Control
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              disabled={processing}
            />
            <Form.Text className="text-muted">
              Seleccione un documento PDF (reglamento interno, manual, contrato, etc.) para prepararlo para consultas inteligentes.
            </Form.Text>
          </Form.Group>

          {/* ✅ Nuevo campo para el título del documento */}
          <Form.Group className="mb-3">
            <Form.Label>Título del Documento</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ingrese el título del documento"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={processing}
              required
            />
            <Form.Text className="text-muted">
              Este título se usará para identificar el documento en las consultas de IA. Se pre-llena con el nombre del archivo.
            </Form.Text>
          </Form.Group>

          <div className="d-flex gap-2 mb-3">
            <Button
              variant="success"
              size="lg"
              onClick={handleProcessCompleteDocument}
              disabled={!file || !pdfJsLoaded || processing}
            >
              {processing ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Procesando documento...
                </>
              ) : (
                "🚀 Procesar Documento Completo"
              )}
            </Button>
          </div>

          {processing && (
            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex align-items-center mb-2">
                  <Spinner animation="border" className="me-3" />
                  <div>
                    <strong>Procesando documento...</strong>
                    <div className="small text-muted mt-1">{statusMessage}</div>
                  </div>
                </div>
                <ProgressBar now={progress} className="mb-2" />
                <div className="small text-center text-muted">
                  {progress}% completado
                </div>
              </Card.Body>
            </Card>
          )}

          {success && (
            <Alert variant="success">
              <Alert.Heading>¡Documento procesado exitosamente!</Alert.Heading>
              <p>
                El documento está ahora disponible para consultas de IA. Los
                usuarios pueden hacer preguntas sobre su contenido y recibir
                respuestas precisas.
              </p>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger">
          <Alert.Heading>Error al procesar el documento</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
    </Container>
  );
};

PdfTextExtractor.defaultProps = {
  ruc: "",
};

export default PdfTextExtractor;
